import { Blueprint, BlueprintStep, BlueprintValue, Side, Zone, ZoneIdentifier } from '@ekkogmbh/apisdk';
import { Add, ArrowBackIos, ArrowDownward, ArrowForward, ArrowForwardIos } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from '../../Common/Components/Forms/StyledTextField';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import { ZonePicker } from 'src/ZoneManagement/ZonePicker';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import usePagination from 'src/Common/Providers/PaginationProvider';
import BlueprintValueTextField from './BlueprintValueTextField';

interface Props {
  closeHandler: () => void;
  zoneIdentifier?: Pick<Zone, 'name' | 'coordinate'>;
  blueprint?: Blueprint;
}

const PanelComponent = ({ blueprint, zoneIdentifier, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { currentStep, enableNext, enableSave } = usePanel();
  const { refresh } = usePagination();
  const [currentBlueprintStep, setCurrentBlueprintStep] = useState<number>();

  const [name, setName] = useState<string>('');
  const [blueprintSteps, setBlueprintSteps] = useState<BlueprintStep[]>([]);
  const [zone, setZone] = useState<ZoneIdentifier>();
  const [isOnce, setOnce] = useState<boolean>(false);

  const [, startLoading] = useRequest();

  const blueprintStep = useMemo(() => {
    if (currentBlueprintStep !== undefined) {
      return blueprintSteps[currentBlueprintStep];
    }

    return undefined;
  }, [currentBlueprintStep, blueprintSteps]);

  const savePayload = useMemo(() => {
    if (name === '' || zone === undefined || blueprintSteps.length === 0) {
      return undefined;
    }

    return { name, zone, steps: blueprintSteps, once: isOnce } as Blueprint;
  }, [name, zone, blueprintSteps, isOnce]);

  const reset = useCallback(() => {
    setName(blueprint?.name ?? '');
    setBlueprintSteps(
      blueprint?.steps ?? [
        {
          source: [],
          destination: [],
        },
      ],
    );
    setZone(blueprint?.zone ?? zoneIdentifier);
    setCurrentBlueprintStep(0);
  }, [zoneIdentifier, blueprint]);

  const save = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }

    startLoading(
      () => api.saveBlueprint(savePayload, blueprint !== undefined),
      () => {
        closeHandler();
        refresh();
      },
    );
  }, [savePayload, blueprint, closeHandler]);

  /** on mount / unmount */
  useEffect(() => {
    reset();
  }, [reset]);

  /** reactive button enablement */
  useEffect(() => {
    switch (currentStep) {
      case 0:
        enableNext(name !== '' && zone !== undefined);
        break;
      default:
        enableSave(savePayload !== undefined);
        break;
    }
  }, [currentStep, name, zone, savePayload]);

  /** steps manip */
  const addValue = useCallback(
    (step: number, side: Side, value: Omit<BlueprintValue, 'state'>) => {
      blueprintSteps[step][side].push(value);
      setBlueprintSteps([...blueprintSteps]);
    },
    [blueprintSteps],
  );

  const replaceValue = useCallback(
    (step: number, side: Side, index: number, value: Omit<BlueprintValue, 'state'>) => {
      blueprintSteps[step][side][index] = value;
      setBlueprintSteps([...blueprintSteps]);
    },
    [blueprintSteps],
  );

  const removeValue = useCallback(
    (step: number, side: Side, index: number) => {
      blueprintSteps[step][side].splice(index, 1);
      setBlueprintSteps([...blueprintSteps]);
    },
    [blueprintSteps],
  );

  const addStep = useCallback(() => {
    blueprintSteps.push({ source: [], destination: [] });
    setBlueprintSteps([...blueprintSteps]);
  }, [blueprintSteps]);

  const removeStep = useCallback(
    (index: number) => {
      blueprintSteps.splice(index, 1);
      setBlueprintSteps([...blueprintSteps]);
    },
    [blueprintSteps],
  );
  /** --- */

  const valueElement = useCallback(
    (side: Side, index: number) => {
      if (blueprintStep === undefined) {
        return <Skeleton />;
      }
      const value = blueprintStep[side][index];

      return (
        <ListItem key={`${currentBlueprintStep}-${side}-${index}-input`} dense>
          <BlueprintValueTextField
            blueprintValue={value}
            onChange={(bpv) => {
              replaceValue(currentBlueprintStep!, side, index, bpv);
            }}
            onRemove={() => {
              removeValue(currentBlueprintStep!, side, index);
            }}
          />
        </ListItem>
      );
    },
    [blueprintStep, currentBlueprintStep, replaceValue, removeValue],
  );

  const valueList = useCallback(
    (side: Side) => {
      if (blueprintStep === undefined) {
        return <Skeleton />;
      }
      return (
        <List dense sx={{ xs: { maxHeight: '33vh', overflow: 'auto' }, sm: { maxHeight: '66vh', overflow: 'auto' } }}>
          {blueprintStep[side].map((_, i) => valueElement(side, i))}
          <ListItem key={'add-item'}>
            <ListItemButton
              alignItems={'center'}
              onClick={() => addValue(currentBlueprintStep!, side, { amount: 1, value: '', metadata: {} })}
            >
              <Button fullWidth variant={'outlined'} color={'secondary'}>
                <Add />
              </Button>
            </ListItemButton>
          </ListItem>
        </List>
      );
    },
    [valueElement, addValue, currentBlueprintStep, blueprintStep],
  );

  const stepElement = useMemo(() => {
    if (blueprintStep === undefined) {
      return <></>;
    }
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'center', sm: 'flex-start' }}
        justifyContent={'center'}
      >
        <Stack direction={'column'} spacing={0} alignItems={'stretch'}>
          <StyledFormHeader label={'source'} />
          {valueList(Side.SOURCE)}
        </Stack>
        <Box display={{ xs: 'none', sm: 'block' }}>
          <ArrowForward color={'primary'} />
        </Box>
        <Box display={{ xs: 'block', sm: 'none' }}>
          <ArrowDownward color={'primary'} />
        </Box>
        <Stack direction={'column'} spacing={0} alignItems={'stretch'}>
          <StyledFormHeader label={'destination'} />
          {valueList(Side.DESTINATION)}
        </Stack>
      </Stack>
    );
  }, [valueList, blueprintStep]);

  const stepPageControl = useMemo(() => {
    const counterLabel = (
      <Stack direction={'row'}>
        <Typography>{currentBlueprintStep === undefined ? '-' : (currentBlueprintStep + 1).toString()}</Typography>
        <Typography>{'/'}</Typography>
        <Typography>{blueprintSteps.length === 0 ? '-' : blueprintSteps.length.toString()}</Typography>
      </Stack>
    );
    return (
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-evenly'}>
        <Button
          color={'error'}
          variant={'outlined'}
          disabled={blueprintSteps.length <= 1}
          onClick={() => {
            setCurrentBlueprintStep(Math.max(0, currentBlueprintStep! - 1));
            removeStep(currentBlueprintStep!);
          }}
        >
          {'remove'}
        </Button>
        <Stack direction={'row'} justifyContent={'center'} spacing={2}>
          <IconButton
            disabled={currentBlueprintStep === 0}
            onClick={() => setCurrentBlueprintStep(currentBlueprintStep! - 1)}
          >
            <ArrowBackIos />
          </IconButton>
          <Stack direction={'column'} alignItems={'center'} justifyContent={'flex-start'}>
            <Typography variant={'overline'} color={'primary'}>
              {'step'}
            </Typography>
            {counterLabel}
          </Stack>
          <IconButton
            disabled={currentBlueprintStep! + 1 === blueprintSteps.length}
            onClick={() => setCurrentBlueprintStep(currentBlueprintStep! + 1)}
          >
            <ArrowForwardIos />
          </IconButton>
        </Stack>
        <Button
          color={'secondary'}
          variant={'outlined'}
          onClick={() => {
            addStep();
            setCurrentBlueprintStep(blueprintSteps.length - 1);
          }}
        >
          {'add'}
        </Button>
      </Stack>
    );
  }, [blueprintSteps.length, removeStep, addStep, currentBlueprintStep]);

  const namingStep: StepDefinition = useMemo(() => {
    const toggleOnce = (): void => {
      setOnce(!isOnce);
    };

    return {
      title: 'Blueprint',
      description: <NamedCoordinateDescription name={name} coordinate={zone?.coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            fullWidth
            type={'text'}
            label={'Name'}
            value={name}
            disabled={blueprint !== undefined}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          {zoneIdentifier === undefined && (
            <ZonePicker
              selected={zone}
              onChange={(zone) => {
                setZone(zone);
              }}
              disabled={blueprint !== undefined}
            />
          )}
          <StyledCheckbox label={'Once'} value={isOnce} onChange={toggleOnce} />
        </ReactivePanelColumn>
      ),
    };
  }, [isOnce, name, zone, blueprint]);

  const stepsStep: StepDefinition = useMemo(
    () => ({
      title: 'Steps',
      contentCallback: () => (
        <Stack direction={'column'} alignItems={'stretch'} justifyContent={'flex-start'} spacing={2}>
          {stepPageControl}
          <Divider />
          {stepElement}
        </Stack>
      ),
    }),
    [currentBlueprintStep, stepPageControl, stepElement],
  );

  return (
    <ContentPanel
      initialStep={blueprint !== undefined ? 1 : 0}
      steps={[namingStep, stepsStep]}
      onCancel={closeHandler}
      onSave={save}
      onReset={reset}
    />
  );
};

export const BlueprintPanel = PanelComponent;
